import { MICRONUTRIENTS, SOURCE_NAMES } from "./nutrition-model";
import { gradeName } from "./grade-names";

const VERDICT_ICONS = { HEALTHY: "✓", MODERATE: "!", UNHEALTHY: "✕", UNKNOWN: "?" };
const FLAG_COLORS = { OK: "#34A853", LIMIT: "#F4A100", AVOID: "#D93025" };
const CONCERN_COLORS = { LOW: "#34A853", MODERATE: "#F4A100", HIGH: "#D93025" };
const LIGHT_NAMES = { fat: "Fat", saturatedFat: "Sat. fat", sugar: "Sugars", salt: "Salt" };
const LEVEL_TEXT = { LOW: "Low", MEDIUM: "Medium", HIGH: "High", UNKNOWN: "?" };

/** [form field, label, unit] in the order of a pack's nutrition table. */
const FACTS = [
  ["calories", "Energy", "kcal"],
  ["protein", "Protein", "g"],
  ["carbs", "Carbohydrates", "g"],
  ["sugar", "Sugars", "g"],
  ["addedSugar", "Added sugars", "g"],
  ["fat", "Fat", "g"],
  ["saturatedFat", "Saturated fat", "g"],
  ["transFat", "Trans fat", "g"],
  ["fiber", "Fibre", "g"],
  ["cholesterol", "Cholesterol", "mg"],
  ["sodium", "Sodium", "mg"],
];

/**
 * The product screen as a customer sees it: the healthy / unhealthy
 * conclusion first, then why, then what the product is made of. Built only
 * from the nutrition service's health report and the label being edited, so
 * what the admin sees here is what the customer apps receive.
 */
function CustomerPreview({ report, name, brand, imageUrl, form }) {
  const rated = report.eatProteinScore !== null;
  const verdict = report.verdict ?? { code: "UNKNOWN", label: report.label, summary: report.advice };
  const ingredients = report.ingredients ?? [];
  const additives = report.additives.items;
  const unit = form.isBeverage ? "100 ml" : "100 g";
  const scale = 100 / (Number(form.basisGrams) || 100);
  const per100 = (value) => (value === "" || isNaN(Number(value)) ? null : Math.round(Number(value) * scale * 10) / 10);
  // Values entered for a whole pack or kit: graded per 100 g, shown with the complete weight too.
  const packGrams = Number(form.basisGrams) > 100 ? Number(form.basisGrams) : null;
  const whole = (value) => (value === "" || isNaN(Number(value)) ? null : Math.round(Number(value) * 10) / 10);
  const packUnit = form.isBeverage ? "ml" : "g";
  // Vitamins and minerals entered for this product; the rest are left out, not shown as 0.
  const micros = MICRONUTRIENTS.filter(([code]) => per100(form.micronutrients[code] ?? "") !== null);
  // Two decimals: µg of B12 or mg of copper would round to 0 otherwise.
  const micro = (value, factor) => Math.round(Number(value) * factor * 100) / 100;

  return (
    <div className="nutri-cv">
      <div className="nutri-cv-product">
        {imageUrl && <img src={imageUrl} alt="" />}
        <div>
          <strong>{name || "Product name"}</strong>
          {brand && <div className="nutri-muted">{brand}</div>}
        </div>
      </div>

      <div className="nutri-cv-verdict" style={{ background: report.color }}>
        <span className="nutri-cv-icon">{VERDICT_ICONS[verdict.code]}</span>
        <div>
          <div className="nutri-cv-verdict-label">{verdict.label}</div>
          <small>{rated ? `${gradeName(report.grade)} · ${report.eatProteinScore}/100` : "Not yet rated"}</small>
          {packGrams && <small className="d-block">Rated per {unit} · whole pack {packGrams} {packUnit}</small>}
        </div>
      </div>
      <p className="nutri-cv-summary">{verdict.summary}</p>

      {report.highlights.length > 0 && (
        <ul className="nutri-list nutri-good">
          {report.highlights.slice(0, 4).map((h) => <li key={h}>{h}</li>)}
        </ul>
      )}
      {report.warnings.length > 0 && (
        <ul className="nutri-list nutri-bad">
          {report.warnings.slice(0, 4).map((w) => <li key={w}>{w}</li>)}
        </ul>
      )}

      <div className="nutri-lights">
        {Object.entries(report.trafficLights).map(([key, light]) => (
          <div key={key} className="nutri-light" style={{ borderColor: light.color }}>
            <strong>{LIGHT_NAMES[key]}</strong>
            <span style={{ color: light.color }}>{LEVEL_TEXT[light.level]}</span>
          </div>
        ))}
      </div>

      <h6>What's inside</h6>
      {ingredients.length === 0 ? (
        <p className="nutri-muted">Ingredients are not available for this product.</p>
      ) : (
        <ul className="nutri-cv-items">
          {ingredients.map((item) => (
            <li key={item.name}>
              <span className="nutri-cv-dot" style={{ background: FLAG_COLORS[item.flag] }} />
              <div>
                {item.name}
                {item.note && <small>{item.note}</small>}
              </div>
            </li>
          ))}
        </ul>
      )}

      <h6>Added chemicals (additives)</h6>
      {additives.length === 0 ? (
        <p className="nutri-muted">
          {ingredients.length > 0 ? "None found in the ingredient list." : "Unknown until the ingredients are entered."}
        </p>
      ) : (
        <ul className="nutri-cv-items">
          {additives.map((a) => (
            <li key={a.code}>
              <span className="nutri-cv-dot" style={{ background: CONCERN_COLORS[a.concern] }} />
              <div>
                {a.name ? `${a.name} (${a.code})` : a.code}
                <small>{[a.purpose, a.advice].filter(Boolean).join(" · ")}</small>
              </div>
            </li>
          ))}
        </ul>
      )}

      {report.allergens.length > 0 && (
        <p className="nutri-cv-allergens">
          <strong>Allergens:</strong> {report.allergens.map((a) => a.toLowerCase().replace(/_/g, " ")).join(", ")}
        </p>
      )}

      <h6>Nutrition per {unit}{packGrams && ` and per pack (${packGrams} ${packUnit})`}</h6>
      <table className="table table-sm nutri-cv-facts">
        {packGrams && (
          <thead>
            <tr>
              <th />
              <th>Per {unit}</th>
              <th>Whole pack ({packGrams} {packUnit})</th>
            </tr>
          </thead>
        )}
        <tbody>
          {FACTS.map(([field, label, factUnit]) => {
            const value = per100(form[field]);
            const total = whole(form[field]);
            return (
              <tr key={field}>
                <td>{label}</td>
                <td>{value === null ? "–" : `${value} ${factUnit}`}</td>
                {packGrams && <td>{total === null ? "–" : `${total} ${factUnit}`}</td>}
              </tr>
            );
          })}
        </tbody>
      </table>

      <h6>Vitamins &amp; minerals per {unit}</h6>
      {micros.length === 0 ? (
        <p className="nutri-muted">Vitamin and mineral values are not available for this product yet.</p>
      ) : (
        <table className="table table-sm nutri-cv-facts">
          <tbody>
            {micros.map(([code, label, microUnit]) => (
              <tr key={code}>
                <td>{label}</td>
                <td>{`${micro(form.micronutrients[code], scale)} ${microUnit}`}</td>
                {packGrams && <td>{`${micro(form.micronutrients[code], 1)} ${microUnit}`}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <p className="nutri-muted mb-0">
        Nutrition data from {SOURCE_NAMES[form.source] ?? "EatProtein"}
        {form.isVerified ? " · verified by EatProtein" : " · not yet verified"}
      </p>
    </div>
  );
}

export default CustomerPreview;
